import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useTranslation } from "@me8eon/translation";
import { useLanguage } from "@me8eon/language";
import { DashboardUniqueUsers, UniqueUsersGranularity, UniqueUsersPoint, UniqueUsersTrend } from "../../../services/dashboardApi";

type Point = { label: string; value: number };

const RED = "#ea1b0a";
const GRID = "#e5e7eb";
const MUTED = "#6b7280";

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** API dates are "yyyy-MM-dd" bucket starts (UTC calendar date) — "Aug 14" for day/week buckets. */
function dayLabel(dateStr: string): string {
    const d = new Date(`${dateStr}T00:00:00Z`);
    return `${MONTH_ABBR[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(2, "0")}`;
}
/** "yyyy-MM-dd" (first of month) → "Oct 25" for month buckets. */
function monthLabel(dateStr: string): string {
    const d = new Date(`${dateStr}T00:00:00Z`);
    return `${MONTH_ABBR[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(-2)}`;
}

/** Maps one granularity's API series to the chart's {label, value} points (empty while loading). */
function toPoints(unit: UniqueUsersGranularity, series: DashboardUniqueUsers[UniqueUsersGranularity] | undefined): Point[] {
    if (!series) return [];
    const label = unit === "month" ? monthLabel : dayLabel;
    return series.points.map((p) => ({ label: label(p.date), value: p.count }));
}

// language from @me8eon/language is a bare 2-letter code — map to a real BCP-47 tag for Intl.
const INTL_LOCALE: Record<string, string> = { en: "en-US", de: "de-DE", sv: "sv-SE", ro: "ro-RO", cz: "cs-CZ", it: "it-IT", hu: "hu-HU", nl: "nl-NL", pl: "pl-PL" };
function intlLocale(lang: string): string {
    return INTL_LOCALE[lang] ?? "en-US";
}

/** "yyyy-MM-dd" → localized "Sep 9" for the day-granularity trend tooltip. */
function trendDayLabel(dateStr: string, locale: string): string {
    const d = new Date(`${dateStr}T00:00:00Z`);
    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", timeZone: "UTC" }).format(d);
}
/** "yyyy-MM-dd" (Monday bucket start) → localized "Sep 1–7" or, across a month boundary, "Aug 29 – Sep 4". */
function trendWeekLabel(dateStr: string, locale: string): string {
    const start = new Date(`${dateStr}T00:00:00Z`);
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    if (start.getUTCMonth() === end.getUTCMonth() && start.getUTCFullYear() === end.getUTCFullYear()) {
        const month = new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" }).format(start);
        return `${month} ${start.getUTCDate()}–${end.getUTCDate()}`;
    }
    const from = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", timeZone: "UTC" }).format(start);
    const to = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", timeZone: "UTC" }).format(end);
    return `${from} - ${to}`;
}
/** "yyyy-MM-dd" (first of month) → localized full month name, e.g. "August". */
function trendMonthLabel(dateStr: string, locale: string): string {
    const d = new Date(`${dateStr}T00:00:00Z`);
    return new Intl.DateTimeFormat(locale, { month: "long", timeZone: "UTC" }).format(d);
}

/** Same last-completed-vs-prior-completed bucket the backend trend itself compares (see dashboard.service.ts). */
function buildTrendTooltip(unit: UniqueUsersGranularity, points: UniqueUsersPoint[], locale: string, lead: string, connector: string): string {
    const n1 = points[points.length - 2];
    const n2 = points[points.length - 3];
    if (!n1 || !n2) return "";
    const label = unit === "day" ? trendDayLabel : unit === "week" ? trendWeekLabel : trendMonthLabel;
    return `${lead} ${label(n1.date, locale)} ${connector} ${label(n2.date, locale)}`;
}

const TICK_STYLE = { fontSize: 12, fontFamily: "sans-serif", fill: MUTED };
let measureCanvas: HTMLCanvasElement | null = null;

/** Pixel width of a string as it will actually render, used to auto-size the Y-axis (canvas measureText, not a character-count guess). */
function measureTextWidth(text: string, fontSize: number, fontFamily: string): number {
    measureCanvas ??= document.createElement("canvas");
    const ctx = measureCanvas.getContext("2d");
    if (!ctx) return text.length * (fontSize * 0.6);
    ctx.font = `${fontSize}px ${fontFamily}`;
    return ctx.measureText(text).width;
}

/** Widens the Y-axis reserved space to fit the largest tick label (otherwise recharts clips digits, e.g. "12000" showing as "2000"). Reuses TICK_STYLE so the measurement always matches what's actually rendered. */
function yAxisWidth(data: Point[]): number {
    const maxValue = data.reduce((max, p) => Math.max(max, p.value), 0);
    const labelWidth = measureTextWidth(maxValue.toLocaleString(), TICK_STYLE.fontSize, TICK_STYLE.fontFamily);
    return Math.max(36, Math.ceil(labelWidth) + 12);
}

const UsersChart: React.FC<{ data: Point[] }> = ({ data }) => (
    <ResponsiveContainer width="100%" height={160}>
        <BarChart data={data} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={GRID} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: MUTED }} axisLine={{ stroke: GRID }} tickLine={false} />
            <YAxis domain={[0, "auto"]} allowDecimals={false} tick={TICK_STYLE} axisLine={false} tickLine={false} width={yAxisWidth(data)} />
            <Tooltip cursor={{ fill: "rgba(234, 27, 10, 0.06)" }} contentStyle={{ borderRadius: 8, border: `1px solid ${GRID}`, fontSize: 12 }} />
            <Bar dataKey="value" fill={RED} radius={[3, 3, 0, 0]} maxBarSize={26} />
        </BarChart>
    </ResponsiveContainer>
);

/** Renders nothing until a trend is available (no prior-window data yet, e.g. thin history). */
const TrendBadge: React.FC<{ trend: UniqueUsersTrend; tooltip: string }> = ({ trend, tooltip }) => {
    if (!trend) return null;
    return (
        <span className={`po-users-trend po-users-trend--${trend.direction}`} title={tooltip}>
            {trend.direction === "up" ? "↗ +" : "↘ -"}{trend.pct}%
        </span>
    );
};

/** "Unique Users" section (Overview, after the KPI grid) — per day/week/month bar charts. */
const UniqueUsersSection: React.FC<{ data: DashboardUniqueUsers | null }> = ({ data }) => {
    const t = useTranslation();
    const [language] = useLanguage();
    const tp = (key: string) => t(`productDashboard.overview.${key}`);
    const locale = intlLocale(language);
    const trendLead = tp("uniqueUsersTrendTooltipLead");
    const trendConnector = tp("uniqueUsersTrendTooltipConnector");

    const cards = [
        { key: "day" as const, label: tp("usersPerDay"), data: toPoints("day", data?.day), trend: data?.day.trend ?? null, tooltip: buildTrendTooltip("day", data?.day.points ?? [], locale, trendLead, trendConnector) },
        { key: "week" as const, label: tp("usersPerWeek"), data: toPoints("week", data?.week), trend: data?.week.trend ?? null, tooltip: buildTrendTooltip("week", data?.week.points ?? [], locale, trendLead, trendConnector) },
        { key: "month" as const, label: tp("usersPerMonth"), data: toPoints("month", data?.month), trend: data?.month.trend ?? null, tooltip: buildTrendTooltip("month", data?.month.points ?? [], locale, trendLead, trendConnector) },
    ];

    return (
        <section className="po-users-section">
            <div className="po-users-section__head">
                <h2>{tp("uniqueUsersTitle")}</h2>
                {/* <span className="po-link">{tp("detailedView")} →</span> */}
            </div>
            <div className="po-users-grid">
                {cards.map((c) => (
                    <div className="po-card po-users-card" key={c.key}>
                        <div className="po-users-card__head">
                            <span className="po-users-card__label">{c.label}</span>
                            <TrendBadge trend={c.trend} tooltip={c.tooltip} />
                        </div>
                        <UsersChart data={c.data} />
                    </div>
                ))}
            </div>
        </section>
    );
};

export default UniqueUsersSection;
